/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Cognito Hosted UI (PKCE) configuration.
 *
 * Values come from GET /api/config at runtime rather than a build-time env
 * file, so the same bundle works in dev and production and a client-id change
 * doesn't need a rebuild. None of them are secrets — they end up in the
 * browser's URL bar during sign-in either way.
 */

import type { UserManagerSettings } from "oidc-client-ts";

export interface RuntimeConfig {
  cognitoDomain: string;
  cognitoRegion: string;
  userPoolId: string;
  webClientId: string;
  configured: boolean;
}

export async function fetchConfig(): Promise<RuntimeConfig> {
  const res = await fetch("/api/config");
  if (!res.ok) throw new Error("Couldn't load app configuration.");
  return res.json() as Promise<RuntimeConfig>;
}

/**
 * The server already returns `cognitoDomain` as a full origin (see
 * normalizeCognitoDomain in server/web_support.ts). This is belt-and-braces for
 * an older API still returning a bare host, and it is why nothing here
 * concatenates a scheme: doing that against a value that already had one
 * produced "https://https//…" and a dead sign-in link.
 */
function hostedUI(domain: string): string {
  const d = (domain ?? "").trim().replace(/\/+$/, "");
  if (!d) return "";
  return /^https?:\/\//i.test(d) ? d : `https://${d}`;
}

export function oidcSettings(cfg: RuntimeConfig): UserManagerSettings {
  const authority =
    `https://cognito-idp.${cfg.cognitoRegion}.amazonaws.com/${cfg.userPoolId}`;
  const ui = hostedUI(cfg.cognitoDomain);
  return {
    authority,
    client_id: cfg.webClientId,
    redirect_uri: `${window.location.origin}/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: "code",
    scope: "openid email profile",
    // Cognito's discovery document omits the Hosted UI endpoints, so they are
    // supplied explicitly rather than discovered.
    metadata: {
      issuer: authority,
      authorization_endpoint: `${ui}/oauth2/authorize`,
      token_endpoint: `${ui}/oauth2/token`,
      userinfo_endpoint: `${ui}/oauth2/userInfo`,
      end_session_endpoint:
        `${ui}/logout?client_id=${cfg.webClientId}` +
        `&logout_uri=${encodeURIComponent(window.location.origin)}`,
      jwks_uri: `${authority}/.well-known/jwks.json`,
    },
    automaticSilentRenew: true,
    // Tokens live in sessionStorage rather than localStorage: closing the tab
    // ends the session, which is the right default on a shared family laptop.
    userStore: undefined,
    monitorSession: false,
  };
}
